import React, { createContext, useContext, useState, ReactNode } from 'react'

interface LoadingContextType {
  isLoading: boolean
  loadingMessage: string
  isCallActive: boolean
  setLoading: (loading: boolean, message?: string) => void
  startLoading: (message?: string) => void
  stopLoading: () => void
  setCallActive: (active: boolean) => void
}

const LoadingContext = createContext<LoadingContextType | undefined>(undefined)

export const useLoading = () => {
  const context = useContext(LoadingContext)
  if (context === undefined) {
    throw new Error('useLoading must be used within a LoadingProvider')
  }
  return context
}

interface LoadingProviderProps {
  children: ReactNode
}

export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [isCallActive, setIsCallActive] = useState(false)

  const setLoading = (loading: boolean, message: string = '') => {
    setIsLoading(loading)
    setLoadingMessage(message)
  }

  const startLoading = (message: string = 'Loading...') => {
    setIsLoading(true)
    setLoadingMessage(message)
  }

  const stopLoading = () => {
    setIsLoading(false)
    setLoadingMessage('')
  }

  const setCallActive = (active: boolean) => {
    setIsCallActive(active)
  }

  const value: LoadingContextType = {
    isLoading,
    loadingMessage,
    isCallActive,
    setLoading,
    startLoading,
    stopLoading,
    setCallActive,
  }

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  )
}
